import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { LANG } from '../../language/lang.js';
import { parsePhoneNumberFromString, isValidPhoneNumber } from 'libphonenumber-js';
import { serializeBuilderData } from '../../utils/qrDesign';
import { createThunkErrorHandler } from '../../utils/handleThunkError.js';
import { handlePending } from '../../utils/handlePending.js';
import { generateQR } from '../../http/builderAPI';

const validators = {
    text: (data) => {
        const errors = {};
        if (!data?.content) errors.text = LANG('INPUT_TEXT_ERROR_EMPTY');
        if (data?.content && data.content.text.length > 1200) errors.text = LANG('INPUT_TEXT_ERROR_MAX_LENGTH');
        if (data?.content && data.content.text.length < 1) errors.text = LANG('INPUT_TEXT_ERROR_EMPTY');
        return errors;
    },
    url: (data) => {
        const errors = {};
        if (!data?.content) errors.url = LANG('INPUT_TEXT_ERROR_EMPTY');

        if (data?.content && !/^https?:\/\//i.test(data.content.url)) {
            errors.url = LANG('INPUT_URL_ERROR_INVALID');
        }
        return errors;
    },
    email: (data) => {
        const errors = {};
        if (!data?.content) errors.email = LANG('INPUT_TEXT_ERROR_EMPTY');
        if (data?.content && !data.content.email.trim()) errors.email = LANG('INPUT_TEXT_ERROR_EMPTY');
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (data?.content && !emailPattern.test(data.content.email)) {
            errors.email = LANG('INPUT_EMAIL_ERROR_INVALID');
        }
        if (data?.content && data.content.text && data.content.text.length > 10000) errors.text = LANG('INPUT_TEXT_ERROR_MAX_LENGTH');
        if (data?.content && data.content.subject && data.content.subject.length > 1000) errors.subject = LANG('INPUT_SUBJECT_ERROR_MAX_LENGTH');
        return errors;
    },
    phone: (data) => {
        const errors = {};
        if (!data?.content) errors.phone = LANG('INPUT_TEXT_ERROR_EMPTY');
        if (data?.content && !data.content.phone.trim()) errors.phone = LANG('INPUT_TEXT_ERROR_EMPTY');
        const phone = typeof data?.content?.phone === 'string' ? data.content.phone.trim() : '';
        if (phone) {
            const parsedPhone = parsePhoneNumberFromString(phone);
            if (!parsedPhone || !isValidPhoneNumber(parsedPhone.number)) {
                errors.phone = LANG('ERROR_TEXT_PHONE_NUMBER');
            }
        }
        return errors;
    },
    sms: (data) => {
        const errors = {};
        if (!data?.content) errors.phone = LANG('INPUT_TEXT_ERROR_EMPTY');
        if (data?.content && !data.content.phone.trim()) errors.phone = LANG('INPUT_TEXT_ERROR_EMPTY');
        const phone = typeof data?.content?.phone === 'string' ? data.content.phone.trim() : '';
        if (phone) {
            const parsedPhone = parsePhoneNumberFromString(phone);
            if (!parsedPhone || !isValidPhoneNumber(parsedPhone.number)) {
                errors.phone = LANG('ERROR_TEXT_PHONE_NUMBER');
            }
        }
        if (data?.content && data.content.text && data.content.text.length > 160) errors.text = LANG('INPUT_TEXT_ERROR_MAX_LENGTH');
        return errors;
    },
    location: (data) => {
        const errors = {};
        if (!data?.content) {
            errors.lat = LANG('INPUT_TEXT_ERROR_EMPTY');
            errors.lng = LANG('INPUT_TEXT_ERROR_EMPTY');
        }
        if (data?.content && !data.content.lat.trim()) errors.lat = LANG('INPUT_TEXT_ERROR_EMPTY');
        if (data?.content && !data.content.lng.trim()) errors.lng = LANG('INPUT_TEXT_ERROR_EMPTY');
        return errors;
    },
    wifi: (data) => {
        const errors = {};
        if (!data?.content) {
            errors.name = LANG('INPUT_TEXT_ERROR_EMPTY');
            errors.encryption = LANG('INPUT_TEXT_ERROR_EMPTY');
        }

        if (data?.content && !data.content.name) errors.name = LANG('INPUT_TEXT_ERROR_EMPTY');
        if (data?.content && data.content.password && data.content.password.length > 40) errors.password = LANG('INPUT_TEXT_ERROR_MAX_LENGTH');

        if (data?.content && data.content.encryption !== 'nopass') {
            if (!data.content.password) errors.password = LANG('INPUT_TEXT_ERROR_EMPTY');
            if (data?.content && data.content.password && data.content.password.length > 40) errors.password = LANG('INPUT_TEXT_ERROR_MAX_LENGTH');
            if (data?.content && data.content.password && data.content.password.length < 8) errors.password = LANG('INPUT_TEXT_ERROR_MIN_LENGTH_PASSWORD');

        }

        return errors;
    },
    vcard: (data) => {
        const errors = {};
        if (!data?.content) {
            errors.firstName = LANG('INPUT_TEXT_ERROR_EMPTY');
            errors.lastName = LANG('INPUT_TEXT_ERROR_EMPTY');
        }
        if (data?.content && !data.content.firstName) errors.firstName = LANG('INPUT_TEXT_ERROR_EMPTY');
        if (data?.content && !data.content.lastName) errors.lastName = LANG('INPUT_TEXT_ERROR_EMPTY');
        if (data?.content && data.content.firstName && data.content.firstName.length > 40) errors.firstName = LANG('INPUT_TEXT_ERROR_MAX_LENGTH');
        if (data?.content && data.content.lastName && data.content.lastName.length > 40) errors.lastName = LANG('INPUT_TEXT_ERROR_MAX_LENGTH');

        const phone = typeof data?.content?.phone === 'string' ? data.content.phone.trim() : '';
        if (phone) {
            const parsedPhone = parsePhoneNumberFromString(phone);
            if (!parsedPhone || !isValidPhoneNumber(parsedPhone.number)) {
                errors.phone = LANG('ERROR_TEXT_PHONE_NUMBER');
            }
        }

        const email = typeof data?.content?.email === 'string' ? data.content.email.trim() : '';
        if (email) {
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailPattern.test(email)) {
                errors.email = LANG('INPUT_EMAIL_ERROR_INVALID');
            }
        }

        const urlRaw = typeof data?.content?.url === 'string' ? data.content.url : '';
        const url = urlRaw.trim();
        if (urlRaw.length > 256) {
            errors.url = LANG('INPUT_TEXT_ERROR_MAX_LENGTH');
        } else if (url && !/^https?:\/\//i.test(url)) {
            errors.url = LANG('INPUT_URL_ERROR_INVALID');
        }

        if (typeof data?.content?.address === 'string' && data.content.address.length > 2000) {
            errors.address = LANG('INPUT_TEXT_ERROR_MAX_LENGTH');
        }

        if (typeof data?.content?.title === 'string' && data.content.title.length > 200) {
            errors.title = LANG('INPUT_TEXT_ERROR_MAX_LENGTH');
        }

        const orgRaw = typeof data?.content?.org === 'string'
            ? data.content.org
            : (typeof data?.content?.orgName === 'string' ? data.content.orgName : '');
        if (orgRaw.length > 200) {
            errors.orgName = LANG('INPUT_TEXT_ERROR_MAX_LENGTH');
        }


        return errors;
    }
};

export const submitStep = () => (dispatch, getState) => {
    const { data } = getState().builder;
    const type = data?.type;

    const validator = validators[type];
    if (!validator) {
        dispatch(setErrors({ type: 'Unknown type' }));
        return false;
    }

    const errors = validator(data);

    if (Object.keys(errors).length > 0) {
        dispatch(setErrors(errors));
        return false;
    }

    dispatch(clearErrors());
    dispatch(setStep({ step: 3 }));
    return true;
};

export const sendQrData = createAsyncThunk(
    'builder/sendQrData',
    async (params = {}, { rejectWithValue, dispatch, getState }) => {
        try {

            const state = getState().builder;
            const formData = new FormData();
            const design = state.data?.design || {};
            const {
                logoFile,
                logoPreview,
                logoFileName,
                ...designForServer
            } = design;

            formData.append('content', JSON.stringify(
                {
                    type: state.data?.type,
                    payload: state.data?.content
                }
            ))

            formData.append('design', JSON.stringify(
                {
                    ...designForServer
                }
            ))

            if (logoFile instanceof File) {
                formData.append('logo', logoFile, logoFile.name);
            }


            const data = await generateQR(formData)
            return data


        } catch (e) {
            return rejectWithValue(e?.code || 'Registration failed')
        }
    }
);

const builderQrSlice = createSlice({
    name: 'builder',
    initialState: {
        step: 1,
        isLoading: false,
        errors: {},
        globalError: null,
        data: null,
        generatedUuid: null,
        downloadUrl: null,
    },
    reducers: {

        setData(state, action) {
            state.data = action.payload.data;
            localStorage.setItem(
                'qrBuilder',
                JSON.stringify(serializeBuilderData(action.payload.data))
            );
        },
        setStep(state, action) {
            state.step = action.payload.step;
        },

        resetBuilder: (state) => {
            state.step = 1;
            state.isLoading = false;
            state.errors = {};
            state.globalError = null;
            state.data = null;
            state.generatedUuid = null;
            state.downloadUrl = null;
            localStorage.removeItem('qrBuilder');
        },

        clearErrors: (state) => {
            state.errors = {}
            state.globalError = null
        },
        setErrors: (state, action) => {
            state.errors = action.payload;
        }
    },

    extraReducers: builder => {
        builder

            .addCase(sendQrData.rejected, createThunkErrorHandler("SEND_QR_DATA"))
            .addCase(sendQrData.pending, handlePending)
            .addCase(sendQrData.fulfilled, (state, action) => {
                if (action.payload?.success) {
                    state.globalError = null;
                    state.generatedUuid = action.payload.uuid || null;
                    state.downloadUrl = action.payload.downloadUrl || null;
                } else {
                    state.globalError = LANG('ERROR_TEXT_SEND_QR_DATA');
                    state.generatedUuid = null;
                    state.downloadUrl = null;
                }

            })

    }
});

export const {
    setData,
    setStep,
    resetBuilder,
    clearErrors,
    setErrors
} = builderQrSlice.actions;

export default builderQrSlice.reducer;